'use client'

import { useTransition } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { Link } from '@/app/components/atoms/Link'
import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { LocationPicker } from '@/app/components/shared/LocationPicker'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'
import { buildLocalizedPath } from '@/app/lib/routing'

import { LOCATION_CHANGE_LOADING_LABEL } from './AgendaHeader.constants'
import styles from './Agenda.module.scss'
import type { AgendaHeaderProps } from './AgendaHeader.types'

/**
 * `AgendaHeader` — título opcional del CMS + selector de localización (fiel a
 * "Agenda en {ciudad}" del módulo de referencia `AgendaGrid`, reutilizando
 * nuestro propio `LocationPicker` compartido en vez de duplicar esa lógica) +
 * enlace opcional del CMS.
 *
 * `'use client'`: necesita navegación — aislado aquí, hoja del árbol, para
 * que `Agenda.tsx` siga siendo Server Component (Principio I).
 *
 * `location` llega como prop (la de la URL de la página anfitriona, ver
 * `Agenda.tsx`) — NO de `useCityContext()`: ese contexto global puede quedar
 * desincronizado de la URL real de esta página concreta (cookie de una
 * visita anterior a otra provincia), lo que rompía silenciosamente
 * `buildLocalizedPath` (comparaba contra un segmento que no existía en el
 * `pathname` real y nunca navegaba).
 *
 * Al cambiar de localización: persiste la elección (`setManualLocation`,
 * Server Action) y navega sin recarga completa cuando la página actual tiene
 * un segmento de localización addressable (`buildLocalizedPath`); si no lo
 * tiene, solo persiste la cookie. La navegación resultante recarga la página
 * (Server Component) con la nueva localización, así que el propio módulo — y
 * cualquier `ProductosLocalizados` en la misma página — quedan re-filtrados
 * por ella (ver `withLocation`).
 */
export function AgendaHeader({
  title,
  headingId,
  hasLink,
  linkTitle,
  linkHref,
  openInNewTab,
  location,
}: AgendaHeaderProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isNavigating, startTransition] = useTransition()

  async function handleSelect(newLocation: string) {
    // `await` + invalidar la caché: sin ello, al volver a "todos" el
    // `useGeoLocationSync` de la ruta bare lee la cookie aún antigua (o un
    // destino cacheado) y redirige de vuelta a la provincia anterior.
    await setManualLocation(newLocation)
    invalidateResolvedTargetCache()

    const nextPath = buildLocalizedPath(pathname, location, newLocation)
    if (nextPath === null) return

    const qs = searchParams.toString()
    startTransition(() => {
      // `scroll: false`: mantiene la posición de scroll — el módulo debe
      // seguir en la vista tras el cambio de localización.
      router.push(qs ? `${nextPath}?${qs}` : nextPath, { scroll: false })
    })
  }

  return (
    <>
      {isNavigating && <LoadingOverlay label={LOCATION_CHANGE_LOADING_LABEL} />}
      <div className={styles.header}>
        <h2 id={headingId} className={styles.title}>
          {title}
          {/* Espacio literal (no solo el `gap` visual del flex): el nombre
              accesible del `<h2>` concatena el texto de sus descendientes —
              sin él, el botón del selector quedaría pegado al título para
              lectores de pantalla aunque se vea bien visualmente. */}
          {title && ' '}
          <LocationPicker location={location} onSelect={handleSelect} />
        </h2>

        {hasLink &&
          (openInNewTab ? (
            <Link href={linkHref} external size="s" className={styles.link}>
              {linkTitle}
            </Link>
          ) : (
            <Link href={linkHref} size="s" className={styles.link}>
              {linkTitle}
            </Link>
          ))}
      </div>
    </>
  )
}
